// Lenders — the "who is getting money out right now" database.
//
// Two panels:
//   1. Lender database: platform-wide rows (managed by the master login,
//      read-only for companies) + the company's own lender relationships.
//      Staff keep status current (active / paused / closed) as banks open
//      and tighten their credit boxes.
//   2. BRM reps: each company's Business Relationship Managers — the name,
//      bank, and email behind the one-click "Send to BRM" button.
import { Landmark, Pause, Pencil, Play, Plus, Trash2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { useUser } from "../lib/auth";
import {
  brmReps as brmApi,
  lenders as lendersApi,
  type BrmRep,
  type Lender,
} from "../lib/api";

const money = (n: number | null) =>
  n === null || n === undefined ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function boxSummary(l: Lender): string {
  const bits: string[] = [];
  if (l.min_credit_score) bits.push(`${l.min_credit_score}+ FICO`);
  if (l.min_time_in_business_months) bits.push(`${l.min_time_in_business_months}mo TIB`);
  if (l.min_annual_revenue) bits.push(`${money(l.min_annual_revenue)}/yr rev`);
  if (l.personal_credit_based) bits.push("personal-credit based");
  if (l.min_amount || l.max_amount) bits.push(`${money(l.min_amount)}–${money(l.max_amount)}`);
  if (l.healthcare_friendly) bits.push("healthcare-friendly");
  if (l.allowed_states.length) bits.push(`only: ${l.allowed_states.join(", ")}`);
  if (l.excluded_states.length) bits.push(`not in: ${l.excluded_states.join(", ")}`);
  return bits.join(" · ") || "No credit box set";
}

export default function LendersPage() {
  const { user } = useUser();
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [reps, setReps] = useState<BrmRep[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showAddLender, setShowAddLender] = useState(false);
  const [showAddRep, setShowAddRep] = useState(false);

  // add-lender form
  const [lf, setLf] = useState<any>({ name: "", lender_type: "bank", products: "term_loan", application_url: "", min_credit_score: "", min_time_in_business_months: "", min_annual_revenue: "", min_amount: "", max_amount: "", allowed_states: "", excluded_states: "", healthcare_friendly: false, personal_credit_based: false, funding_speed_days: "", notes: "", platform: false });
  // add-rep form
  const [rf, setRf] = useState({ name: "", bank_name: "", email: "", phone: "", notes: "", is_default: false });

  async function refresh() {
    try { setLenders(await lendersApi.list()); } catch (e: any) { setErr(e?.message || "Could not load lenders"); }
    try { setReps(await brmApi.list()); } catch {}
  }
  useEffect(() => { refresh(); }, []);

  async function toggleStatus(l: Lender) {
    const next = l.status === "active" ? "paused" : "active";
    setBusy(l.id);
    try {
      const updated = await lendersApi.update(l.id, { status: next });
      setLenders(prev => prev.map(x => (x.id === l.id ? updated : x)));
    } catch (e: any) {
      alert(e?.message || "Update failed");
    } finally { setBusy(null); }
  }

  // When set, the lender form edits this row instead of creating a new one.
  const [editingId, setEditingId] = useState<string | null>(null);

  function startEdit(l: Lender) {
    setEditingId(l.id);
    setLf({
      name: l.name || "",
      lender_type: l.lender_type || "bank",
      products: (l.products || []).join(","),
      application_url: l.application_url || "",
      min_credit_score: l.min_credit_score != null ? String(l.min_credit_score) : "",
      min_time_in_business_months: l.min_time_in_business_months != null ? String(l.min_time_in_business_months) : "",
      min_annual_revenue: l.min_annual_revenue != null ? String(l.min_annual_revenue) : "",
      min_amount: l.min_amount != null ? String(l.min_amount) : "",
      max_amount: l.max_amount != null ? String(l.max_amount) : "",
      allowed_states: ((l as any).allowed_states || []).join(","),
      excluded_states: ((l as any).excluded_states || []).join(","),
      healthcare_friendly: !!(l as any).healthcare_friendly,
      personal_credit_based: !!(l as any).personal_credit_based,
      funding_speed_days: (l as any).funding_speed_days != null ? String((l as any).funding_speed_days) : "",
      notes: l.notes || "",
      platform: false,
    });
    setShowAddLender(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function addLender() {
    setBusy("add");
    setErr(null);
    const csv = (s: string) => s.split(",").map(x => x.trim()).filter(Boolean);
    const num = (s: string) => (s === "" ? null : Number(s));
    const payload = {
      name: lf.name,
      lender_type: lf.lender_type,
      products: csv(lf.products),
      application_url: lf.application_url || null,
      min_credit_score: num(lf.min_credit_score),
      min_time_in_business_months: num(lf.min_time_in_business_months) ?? 0,
      min_annual_revenue: num(lf.min_annual_revenue) ?? 0,
      min_amount: num(lf.min_amount),
      max_amount: num(lf.max_amount),
      allowed_states: csv(lf.allowed_states).map(s => s.toUpperCase()),
      excluded_states: csv(lf.excluded_states).map(s => s.toUpperCase()),
      healthcare_friendly: lf.healthcare_friendly,
      personal_credit_based: lf.personal_credit_based,
      funding_speed_days: num(lf.funding_speed_days),
      notes: lf.notes || null,
    };
    try {
      if (editingId) {
        const updated = await lendersApi.update(editingId, payload as any);
        setLenders(prev => prev.map(x => (x.id === editingId ? updated : x)));
      } else {
        const created = await lendersApi.create({ ...payload, ...(lf.platform ? { platform: true } : {}) } as any);
        setLenders(prev => [...prev, created]);
      }
      setShowAddLender(false);
      setEditingId(null);
      setLf({ ...lf, name: "", application_url: "", notes: "" });
    } catch (e: any) {
      setErr(e?.message || (editingId ? "Could not update lender" : "Could not create lender"));
    } finally { setBusy(null); }
  }

  async function addRep() {
    setBusy("add-rep");
    try {
      const created = await brmApi.create({
        name: rf.name, bank_name: rf.bank_name, email: rf.email,
        phone: rf.phone || undefined, notes: rf.notes || undefined, is_default: rf.is_default,
      });
      setReps(prev => [created, ...prev]);
      setShowAddRep(false);
      setRf({ name: "", bank_name: "", email: "", phone: "", notes: "", is_default: false });
    } catch (e: any) {
      alert(e?.message || "Could not add rep");
    } finally { setBusy(null); }
  }

  const input = (v: string, set: (s: string) => void, ph: string, w?: number) => (
    <input className="text-input" value={v} placeholder={ph} onChange={(e) => set(e.target.value)} style={w ? { width: w } : undefined} />
  );

  return (
    <Shell title="Lenders" subtitle="Who's funding right now" actions={
      <button className="primary-button" onClick={() => setShowAddLender(v => !v)}>
        <Plus size={18} aria-hidden="true" /> Add lender
      </button>
    }>
      {err && <p className="error-text">{err}</p>}

      {showAddLender && (
        <div className="panel" style={{ marginBottom: 18 }}>
          <div className="panel-header"><div><h2>{editingId ? `Edit lender — ${lf.name || ""}` : "New lender"}</h2><span className="muted small">Set the credit box — the matcher enforces every field you fill in.</span></div></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            {input(lf.name, s => setLf({ ...lf, name: s }), "Lender name *")}
            <select className="text-input" value={lf.lender_type} onChange={e => setLf({ ...lf, lender_type: e.target.value })}>
              {["bank", "credit_union", "fintech", "sba", "factoring", "mca", "equipment", "private"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {input(lf.products, s => setLf({ ...lf, products: s }), "products (csv): term_loan,line_of_credit,factoring")}
            {input(lf.application_url, s => setLf({ ...lf, application_url: s }), "Application URL (Apply-now target)")}
            {input(lf.min_credit_score, s => setLf({ ...lf, min_credit_score: s }), "Min credit score")}
            {input(lf.min_time_in_business_months, s => setLf({ ...lf, min_time_in_business_months: s }), "Min months in business")}
            {input(lf.min_annual_revenue, s => setLf({ ...lf, min_annual_revenue: s }), "Min annual revenue $")}
            {input(lf.min_amount, s => setLf({ ...lf, min_amount: s }), "Min amount $")}
            {input(lf.max_amount, s => setLf({ ...lf, max_amount: s }), "Max amount $")}
            {input(lf.allowed_states, s => setLf({ ...lf, allowed_states: s }), "Allowed states (csv, blank = nationwide)")}
            {input(lf.excluded_states, s => setLf({ ...lf, excluded_states: s }), "Excluded states (csv)")}
            {input(lf.funding_speed_days, s => setLf({ ...lf, funding_speed_days: s }), "Funding speed (days)")}
            {input(lf.notes, s => setLf({ ...lf, notes: s }), "Notes (box intel, rep info)")}
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
            <label className="muted small" style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={lf.healthcare_friendly} onChange={e => setLf({ ...lf, healthcare_friendly: e.target.checked })} /> Healthcare-friendly
            </label>
            <label className="muted small" style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={lf.personal_credit_based} onChange={e => setLf({ ...lf, personal_credit_based: e.target.checked })} /> Personal-credit based (startups OK)
            </label>
            {user?.is_super_admin && (
              <label className="muted small" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={lf.platform} onChange={e => setLf({ ...lf, platform: e.target.checked })} /> Platform-wide (visible to every company)
              </label>
            )}
            <button className="primary-button" disabled={!lf.name || busy === "add"} onClick={addLender}>
              {busy === "add" ? "Saving…" : "Save lender"}
            </button>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-header">
          <div><h2><Landmark size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> Lender database</h2>
          <span className="muted small">Pause a lender the moment their box tightens — paused lenders never get recommended.</span></div>
        </div>
        {([
          ["Personal credit lenders", lenders.filter((l: any) => l.personal_credit_based)],
          ["Business lenders", lenders.filter((l: any) => !l.personal_credit_based)],
        ] as Array<[string, Lender[]]>).map(([groupLabel, group]) => group.length === 0 ? null : (
        <div key={groupLabel} style={{ marginBottom: 14 }}>
          <h3 style={{ margin: "10px 0 6px", fontSize: 14, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ne-muted)" }}>
            {groupLabel} <span style={{ fontWeight: 400 }}>({group.length})</span>
          </h3>
        <div className="table-list">
          {group.map((l) => (
            <div className="table-row table-row--wide" key={l.id} style={l.status !== "active" ? { opacity: 0.55 } : undefined}>
              <div style={{ minWidth: 0, flex: "1 1 260px" }}>
                <strong>{l.name}</strong>{" "}
                <span className="muted small">
                  {l.lender_type}{l.is_platform ? " · platform" : ""} · {(l.products || []).join(", ")}
                </span>
                <div className="muted small" style={{ marginTop: 2 }}>{boxSummary(l)}</div>
                {l.notes && <div className="muted small" style={{ marginTop: 2, fontStyle: "italic" }}>{l.notes}</div>}
              </div>
              <span className={`pill ${l.status === "active" ? "pill-green" : "pill-red"}`}>{l.status}</span>
              {(!l.is_platform || user?.is_super_admin) && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="ghost-button" disabled={busy === l.id} onClick={() => startEdit(l)} title="Edit lender">
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                  <button className="ghost-button" disabled={busy === l.id} onClick={() => toggleStatus(l)} title={l.status === "active" ? "Pause" : "Activate"}>
                    {l.status === "active" ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
                  </button>
                  <button
                    className="ghost-button"
                    style={{ color: "var(--ne-red)" }}
                    title="Delete lender"
                    onClick={async () => {
                      if (!confirm(`Delete ${l.name}? Prefer pausing unless it's a duplicate.`)) return;
                      try { await lendersApi.remove(l.id); setLenders(prev => prev.filter(x => x.id !== l.id)); }
                      catch (e: any) { alert(e?.message || "Delete failed"); }
                    }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        </div>
        ))}
        {lenders.length === 0 && <p className="muted small">No lenders yet — the platform seed loads on first deploy.</p>}
      </div>

      <div className="panel" style={{ marginTop: 18 }}>
        <div className="panel-header">
          <div><h2><Users size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> Your BRM reps</h2>
          <span className="muted small">The bank relationship managers behind the one-click "Send to BRM" button on each client.</span></div>
          <button className="ghost-button" onClick={() => setShowAddRep(v => !v)}><Plus size={16} aria-hidden="true" /> Add rep</button>
        </div>

        {showAddRep && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 14 }}>
            {input(rf.name, s => setRf({ ...rf, name: s }), "Rep name *")}
            {input(rf.bank_name, s => setRf({ ...rf, bank_name: s }), "Bank they work for *")}
            {input(rf.email, s => setRf({ ...rf, email: s }), "Email *")}
            {input(rf.phone, s => setRf({ ...rf, phone: s }), "Phone")}
            {input(rf.notes, s => setRf({ ...rf, notes: s }), "Notes")}
            <label className="muted small" style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={rf.is_default} onChange={e => setRf({ ...rf, is_default: e.target.checked })} /> Default rep
            </label>
            <button className="primary-button" disabled={!rf.name || !rf.bank_name || !rf.email.includes("@") || busy === "add-rep"} onClick={addRep}>
              {busy === "add-rep" ? "Saving…" : "Save rep"}
            </button>
          </div>
        )}

        <div className="table-list">
          {reps.map((r) => (
            <div className="table-row table-row--wide" key={r.id} style={!r.active ? { opacity: 0.55 } : undefined}>
              <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                <strong>{r.name}</strong> <span className="muted small">{r.bank_name}</span>
                <div className="muted small">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
              </div>
              {r.is_default && <span className="pill pill-green">default</span>}
              <div style={{ display: "flex", gap: 6 }}>
                {!r.is_default && (
                  <button className="ghost-button" onClick={async () => {
                    try { await brmApi.update(r.id, { is_default: true }); refresh(); } catch (e: any) { alert(e?.message); }
                  }}>Make default</button>
                )}
                <button className="ghost-button" style={{ color: "var(--ne-red)" }} onClick={async () => {
                  if (!confirm(`Remove ${r.name}?`)) return;
                  try { await brmApi.remove(r.id); setReps(prev => prev.filter(x => x.id !== r.id)); } catch (e: any) { alert(e?.message); }
                }}><Trash2 size={14} aria-hidden="true" /></button>
              </div>
            </div>
          ))}
          {reps.length === 0 && <p className="muted small">No reps yet. Add your business relationship manager to enable one-click handoffs.</p>}
        </div>
      </div>
    </Shell>
  );
}
